import { Get, Post } from "./ApiBase";

// ProfileContext API - Extracts structured career data from FullResume
// NEW: Added for Profile Showcase View and evidence-based features

let cachedContext = null;
let cacheResumeId = null;
let cacheTimestamp = null;
const CACHE_TTL_MS = 60000; // Cache for 1 minute

// todo: Whatever calls this needs to be using the existing store, instead of rebuilding it here. Otherwise we have cache incoherence and potential security issues with passing the id around.

export async function GetProfileContext(resumeId, forceRefresh = false) {
  // Return cached data if valid
  if (!forceRefresh && cachedContext && cacheResumeId === resumeId) {
    const now = Date.now();
    if (cacheTimestamp && now - cacheTimestamp < CACHE_TTL_MS) {
      return { success: true, data: cachedContext };
    }
  }

  const result = await Get(
    `/api/ProfileContext?resumeId=${encodeURIComponent(resumeId)}`
  );

  if (result.success && result.data) {
    cachedContext = result.data;
    cacheResumeId = resumeId;
    cacheTimestamp = Date.now();
  }

  return result;
}

export function ClearProfileContextCache() {
  cachedContext = null;
  cacheResumeId = null;
  cacheTimestamp = null;
}

// Skill Evidence Map — LLM-assessed proven / demonstrated / claimed tiers.
// Stateless on the backend; CareerProfile.vue caches the result in
// localStorage keyed by a hash of the user's skills + achievements, so this
// only re-runs when the profile actually changes.
export async function GetSkillEvidenceMap(resumeId) {
  return await Get(
    `/api/SkillEvidenceMap?resumeId=${encodeURIComponent(resumeId)}`
  );
}

// PR-SKILL-SEED: flip the durable, per-user one-time flag after the Career
// Profile has seeded each technical skill's proficiency from the deterministic
// evidence map. Persists server-side so the seed never re-runs across devices.
export async function MarkSkillsSeededFromEvidence() {
  return await Post("/api/MarkSkillsSeededFromEvidence", {});
}

// Helper functions to extract specific data from ProfileContext

export async function GetProfileSkills(resumeId) {
  const result = await GetProfileContext(resumeId);
  if (!result.success) return { success: false, skills: [] };
  return {
    success: true,
    skills: result.data?.skills || {
      hard: [],
      soft: [],
      technical: [],
      totalCount: 0
    }
  };
}

export async function GetProfileWorkExperience(resumeId) {
  const result = await GetProfileContext(resumeId);
  if (!result.success) return { success: false, workExperience: [] };
  return {
    success: true,
    workExperience: result.data?.workExperience || []
  };
}

export async function GetProfileAchievements(resumeId) {
  const result = await GetProfileContext(resumeId);
  if (!result.success) return { success: false, achievements: [] };

  // Combine achievements from work experience and small wins
  const workAchievements = (result.data?.workExperience || []).flatMap(work =>
    (work.achievements || []).map(a => ({
      ...a,
      source: "work",
      company: work.company,
      title: (work.positions && work.positions[0]?.title) || ""
    }))
  );

  const smallWins = (result.data?.smallWins || []).map(sw => ({
    id: sw.id,
    summary: sw.description,
    situation: sw.context,
    action: sw.action,
    result: sw.outcome,
    source: "smallWin",
    category: sw.category,
    eventDate: sw.eventDate
  }));

  return {
    success: true,
    achievements: [...workAchievements, ...smallWins]
  };
}

export async function GetProfileCompleteness(resumeId) {
  const result = await GetProfileContext(resumeId);
  if (!result.success) return { success: false, completeness: null };
  return {
    success: true,
    completeness: {
      score: result.data?.completenessScore || 0,
      maxScore: 100,
      percentage: result.data?.completenessScore || 0
    }
  };
}

export async function GetProfileSummary(resumeId) {
  const result = await GetProfileContext(resumeId);
  if (!result.success) return { success: false, summary: null };

  const data = result.data;
  const first = data?.name?.firstName || "";
  const last = data?.name?.lastName || "";
  const city = data?.address?.city || "";
  const state = data?.address?.state || "";
  return {
    success: true,
    summary: {
      displayName: `${first} ${last}`.trim(),
      location: [city, state].filter(s => s).join(", "),
      completeness: data?.completenessScore || 0
    }
  };
}
